import { FC, useMemo, useState } from 'react'
import { CFDFile, WorkflowRunGroup } from '@aero-platform/shared'
import toast from 'react-hot-toast'
import { InputTextarea } from 'primereact/inputtextarea'
import {
  addSimChangeMetadata,
  useAddWorkflowRunGroup,
  useRefreshMethodology
} from '../../api/submission-API'
import { RunStatus, WorkflowSubmission } from '../../types/types'
import { computeSupersededIds } from '../../types/submission-util'
import Modal from '../Modal'
import SimTemplateDropdown from './SimTemplateDropdown'
import { determineWorkflowGroupStatus } from './CaseRowExpansion'

type SubmitAllModalProps = {
  submission: WorkflowSubmission
  onClose: () => void
  onComplete: () => void
  // systemRunIds chosen in the cases table. When non-empty, only these cases are resubmitted.
  selectedSystemRunIds?: string[]
}

const SubmitAllModal: FC<SubmitAllModalProps> = ({
  submission,
  onClose,
  onComplete,
  selectedSystemRunIds
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<CFDFile | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [reason, setReason] = useState('')
  const [reasonTouched, setReasonTouched] = useState(false)
  const [, refreshMethodology] = useRefreshMethodology()
  const [, addWorkflowRunGroup] = useAddWorkflowRunGroup()

  const isReasonValid = reason.trim().length > 0

  // Active (non-superseded) cases in a terminal state — the same eligibility the single-case
  // resubmit uses. These are the cases a "resubmit all" actually re-runs.
  const eligibleCases = useMemo<WorkflowRunGroup[]>(() => {
    // Submission.tsx stores submissionRunGroups reversed (newest-first); restore chronological
    // order so computeSupersededIds (which treats the last occurrence as active) is correct.
    const allRunGroups = [...(submission.submissionRunGroups || [])]
      .reverse()
      .flatMap(srg => srg.workflowRunGroups || [])
    const supersededIds = computeSupersededIds(allRunGroups)
    const eligible = allRunGroups.filter(wrg => {
      if (wrg.name === 'All' || supersededIds.has(wrg.systemRunId)) return false
      const status = determineWorkflowGroupStatus(wrg)
      return (
        status === RunStatus.COMPLETED ||
        status === RunStatus.ERROR ||
        status === RunStatus.WARN_ON_ERROR ||
        status === RunStatus.CANCELLED ||
        wrg.overallStatus === RunStatus.CANCELLED
      )
    })
    // Restrict to the user's table selection when one exists; otherwise resubmit all eligible.
    if (selectedSystemRunIds && selectedSystemRunIds.length > 0) {
      const selected = new Set(selectedSystemRunIds)
      return eligible.filter(wrg => selected.has(wrg.systemRunId))
    }
    return eligible
  }, [submission, selectedSystemRunIds])

  const handleConfirm = async () => {
    if (!selectedTemplate?.file || !isReasonValid || submitting) return
    if (eligibleCases.length === 0) {
      toast.error('No eligible cases to resubmit.')
      return
    }
    setSubmitting(true)
    const runId = submission._id

    // Refresh methodology to the latest version first, mirroring the single-case resubmit.
    try {
      await (refreshMethodology as any)({ runId })
    } catch (error) {
      console.error('Failed to refresh methodology:', error)
      toast.error('Failed to refresh methodology. Resubmit aborted.')
      setSubmitting(false)
      return
    }

    try {
      // One call submits every eligible case; sourceSystemRunId targets each exact run group so
      // multi-car drafting following-car cases are cloned rather than re-expanded.
      await (addWorkflowRunGroup as any)({
        runId,
        caseNames: eligibleCases.map(c => c.name),
        sourceSystemRunId: eligibleCases.map(c => c.systemRunId),
        simTemplate: selectedTemplate
      })

      // Record the shared reason as a per-case SIM change entry (previousSimFile differs per case).
      await Promise.all(
        eligibleCases.map(c =>
          addSimChangeMetadata(runId, {
            caseName: c.runGroupCase?.name || c.name,
            previousSimFile: c.runGroupCase?.simTemplate?.file || '',
            newSimFile: selectedTemplate.file || '',
            reason: reason.trim(),
            systemRunId: c.systemRunId
          }).catch(err => console.warn('Failed to persist SIM change metadata:', err))
        )
      )

      toast.success(
        `Resubmitted ${eligibleCases.length} case${eligibleCases.length === 1 ? '' : 's'}.`
      )
      onComplete()
    } catch (error) {
      console.error('Error resubmitting all cases:', error)
      toast.error('Failed to resubmit cases.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      title={
        selectedSystemRunIds && selectedSystemRunIds.length > 0
          ? 'Resubmit Selected Cases'
          : 'Resubmit All Cases'
      }
      isOpen={true}
      size="md"
      easyClose={false}
      withAction
      showCancelButton
      confirmButtonCaption="Resubmit"
      cancelButtonCaption="Cancel"
      cancelButtonDisabled={submitting}
      confirmButtonDisabled={
        selectedTemplate === null ||
        !selectedTemplate.file ||
        submitting ||
        !isReasonValid ||
        eligibleCases.length === 0
      }
      onConfirm={handleConfirm}
      onClose={onClose}
      onCancel={submitting ? undefined : onClose}>
      <div className="flex flex-col gap-4 pt-2">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {eligibleCases.length} eligible case{eligibleCases.length === 1 ? '' : 's'} will be
          resubmitted.
        </p>
        {submission.runConfig.overrideSelectedSimFile && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Current:{' '}
            <span className="font-medium">{submission.runConfig.overrideSelectedSimFile}</span>
          </p>
        )}

        {/* SIM Template Dropdown */}
        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-neutral-10 mb-2">
            SIM Template
          </label>
          <SimTemplateDropdown
            submissionId={submission._id}
            submissionName={submission.name}
            value={selectedTemplate}
            onChange={val => {
              setSelectedTemplate(val)
            }}
            placeholder="Select a template..."
            disabled={submitting}
          />
          <div className="text-sm text-gray-500 mt-1">
            {selectedTemplate
              ? `(${selectedTemplate.rootType}-${
                  selectedTemplate.fileType || selectedTemplate.rootType || selectedTemplate.system
                }) ${selectedTemplate.file}`
              : 'None'}
          </div>
        </div>

        {/* Required reason for SIM file change */}
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Reason for resubmission <span className="text-red-500">*</span>
          </label>
          <InputTextarea
            value={reason}
            onChange={e => {
              setReason(e.target.value)
              if (!reasonTouched) setReasonTouched(true)
            }}
            rows={3}
            placeholder="Describe why the run is being resubmitted..."
            className={reasonTouched && !isReasonValid ? 'p-invalid' : ''}
            disabled={submitting}
            pt={{
              root: {
                className: '!bg-transparent'
              }
            }}
          />
          {reasonTouched && !isReasonValid && (
            <small className="text-red-500">A reason is required to resubmit the run.</small>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default SubmitAllModal
